import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import NumberFormat from "react-number-format";
import { Hash } from "../helpers/Helpers";
import { Create, Update, GetOne } from "../services/UsersServices";
import { css } from "@emotion/react";
import BeatLoader from "react-spinners/BeatLoader";
import AlertResponses from "../jsx/components/Alert";
import { useForm, Controller } from "react-hook-form";
import UserContext from "../contexts/UserContext";
import Swal from "sweetalert2";
import ProgressBarComponent from "../jsx/components/ProgressBar";
import styled from "styled-components";
import {
  IconUser,
  IconArrowLeft,
  IconCamera,
  IconCheck,
  IconMail,
  IconPhone,
  IconCalendar,
  IconId,
  IconShield,
  IconToggleLeft,
  IconToggleRight,
} from "@tabler/icons-react";

// Styled Components
const Container = styled.div`
  background: #f8f9fa;

  @media (max-width: 768px) {
    background: #ffffff;
  }
`;

const Header = styled.div`
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 20px rgba(0, 123, 255, 0.2);

  @media (max-width: 768px) {
    padding: 16px;
    position: sticky;
    top: 0;
    z-index: 100;
  }
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const HeaderTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  flex: 1;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const ContentContainer = styled.div`
  padding: 24px;
  max-width: 800px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const ProfileSection = styled.div`
  background: white;
  border-radius: 20px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  text-align: center;

  @media (max-width: 768px) {
    border-radius: 16px;
    padding: 20px;
    margin-bottom: 20px;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  display: inline-block;
  margin-bottom: 20px;
`;

const UserImage = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid #e9ecef;
  background: #f8f9fa;

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
  }
`;

const ImagePlaceholder = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e9ecef, #f8f9fa);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 4px solid #e9ecef;
  color: #6c757d;
  font-size: 40px;

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
    font-size: 36px;
  }
`;

const CameraButton = styled.label`
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: #007bff;
  color: white;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);

  &:hover {
    background: #0056b3;
    transform: scale(1.05);
  }

  input {
    display: none;
  }
`;

const FormContainer = styled.div`
  background: white;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    border-radius: 16px;
    padding: 20px;
  }
`;

const FormTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #212529;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 20px;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 600;
  color: #495057;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const InputContainer = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  font-size: 16px;
  transition: all 0.3s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }

  &.is-invalid {
    border-color: #dc3545;
  }

  &:disabled {
    background: #f8f9fa;
    color: #6c757d;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 14px 16px;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  font-size: 16px;
  transition: all 0.3s ease;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }

  &.is-invalid {
    border-color: #dc3545;
  }
`;

const NumberFormatInput = styled(NumberFormat)`
  width: 100%;
  padding: 14px 16px;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  font-size: 16px;
  transition: all 0.3s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }

  &.is-invalid {
    border-color: #dc3545;
  }

  &[readonly] {
    background: #f8f9fa;
    color: #6c757d;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SaveButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 16px 24px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 24px;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 123, 255, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  &:active {
    transform: translateY(0);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  color: #6c757d;
`;

const StatusToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 12px;
`;

const StatusToggle = styled.div`
  position: relative;
  width: 60px;
  height: 32px;
  background: ${(props) => (props.active ? "#28a745" : "#6c757d")};
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:before {
    content: "";
    position: absolute;
    top: 3px;
    left: ${(props) => (props.active ? "31px" : "3px")};
    width: 26px;
    height: 26px;
    background: white;
    border-radius: 50%;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

const StatusLabel = styled.span`
  font-weight: 600;
  color: #495057;
  font-size: 14px;
`;

const LevelBadge = styled.div`
  background: ${(props) => {
    switch (props.level) {
      case "1":
        return "linear-gradient(135deg, #dc3545, #c82333)";
      case "2":
        return "linear-gradient(135deg, #fd7e14, #e55a00)";
      case "3":
        return "linear-gradient(135deg, #ffc107, #e0a800)";
      case "4":
        return "linear-gradient(135deg, #28a745, #1e7e34)";
      default:
        return "linear-gradient(135deg, #6c757d, #5a6268)";
    }
  }};
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-block;
  margin-top: 8px;
`;

export default function UserDetails(props) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, setUser } = useContext(UserContext);
  const [data, setData] = useState(
    props?.location?.data ? props.location.data : null
  );
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [notify, setNotify] = useState({
    title: "",
    message: "",
    type: "",
    visible: false,
  });
  const {
    register,
    handleSubmit,
    setError,
    setValue,
    control,
    reset,
    getValues,
    watch,
    formState: { errors },
  } = useForm();
  const [isLoading, setIsLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [progress, setProgress] = useState(false);

  const watchedIsActive = watch("is_active");
  const watchedLevel = watch("superuser_level");

  // Função para formatar a data do formato ISO para DD/MM/YYYY
  const formatDate = (isoDate) => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Função para formatar CPF com pontos e traço
  const formatCPF = (cpf) => {
    if (!cpf) return "";
    const cleanCPF = cpf.replace(/\D/g, "");
    return cleanCPF.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  };

  // Função para formatar telefone
  const formatPhone = (phone) => {
    if (!phone) return "";
    const cleanPhone = phone.replace(/\D/g, "");
    return cleanPhone.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  };

  // Função para popular os campos do formulário com os dados obtidos
  const populateFormData = (data) => {
    setValue("first_name", data.first_name || "");
    setValue("last_name", data.last_name || "");
    setValue("email", data.email || "");
    setValue("cpf", formatCPF(data.cpf || ""));
    setValue("phone", formatPhone(data.phone || ""));
    setValue("birthday", data.birthday ? formatDate(data.birthday) : "");
    setValue("superuser_level", data.superuser_level?.toString() || "");
    setValue("is_active", data.is_active?.toString() || "1");
    setValue("account_id", data.account?.toString() || user.account_id);
  };

  // Buscar dados do usuário e lojas disponíveis
  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        try {
          const userData = await GetOne(id);
          setData(userData);
          populateFormData(userData);
          setIsLoading(false);
        } catch (error) {
          console.error("Erro ao buscar usuário:", error);
          setNotify({
            title: "Erro",
            message: "Não foi possível carregar os dados do usuário",
            type: "error",
            visible: true,
          });
          setIsLoading(false);
        }
      } else {
        setData(null);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const override = css`
    display: block;
    margin: 0 auto;
    text-align: center;
    margin-top: 20%;
  `;

  const handleImagePreview = (image) => {
    if (!image) return;

    if (image.size > 2000000) {
      Swal.fire(
        "Atenção!",
        "Imagem muito pesada! Por favor, insira imagens até 2MB.",
        "info"
      );
      return;
    }

    setSelectedFile(image);
    const url = URL.createObjectURL(image);
    setImageFile(url);
  };

  const onSubmit = async (formData) => {
    setProgress(true);
    try {
      const form = new FormData();

      // Formatar a data de aniversário para o formato esperado pela API
      let birthday = "";
      if (formData.birthday) {
        birthday = formData.birthday
          .split(formData.birthday.includes("/") ? "/" : "-")
          .reverse()
          .join("-");
      }

      // Adicionar campos ao FormData
      form.append("first_name", formData.first_name);
      form.append("last_name", formData.last_name);
      form.append("birthday", birthday);
      form.append("phone", formData.phone.replace(/[^0-9]/g, ""));

      // Adiciona a foto se uma nova for selecionada
      if (selectedFile && selectedFile instanceof File) {
        form.append("photo", selectedFile);
        console.log("Enviando arquivo de imagem:", selectedFile.name);
      }

      form.append("superuser_level", formData.superuser_level);
      form.append("is_active", parseInt(formData.is_active));
      form.append("is_superuser", 1);
      form.append("plan_id", user.plan_id);
      form.append("account_id", formData?.account_id || user.account_id);

      if (id) {
        // Atualizar usuário existente
        form.append("id", id);
        await Update(id, form, setUser, setNotify);
      } else {
        // Criar novo usuário
        form.append("email", formData.email);
        form.append("cpf", formData.cpf.replace(/[^0-9]/g, ""));
        await Create(form, setData, setNotify);
      }
    } catch (error) {
      console.error("Erro ao salvar usuário:", error);

      // Tratar erros específicos de validação
      if (error.errors) {
        Object.entries(error.errors).forEach(([field, message]) => {
          setError(field, {
            type: "custom",
            message: message,
          });
        });
      } else {
        setNotify({
          title: "Erro",
          message: "Ocorreu um erro ao salvar os dados",
          type: "error",
          visible: true,
        });
      }
    } finally {
      setProgress(false);
    }
  };

  const getUserImage = () => {
    if (imageFile) {
      return imageFile;
    } else if (data?.photo || data?.photo_url) {
      return Hash("400x400", data.photo ? data.photo : data.photo_url, true);
    }
    return null;
  };

  const getLevelLabel = (level) => {
    const levels = {
      1: "Administrador",
      2: "Gerente",
      3: "Supervisor",
      4: "Atendente",
    };
    return levels[level] || "Não definido";
  };

  if (isLoading) {
    return (
      <Container>
        <LoadingContainer>
          <BeatLoader
            color={"#007bff"}
            loading={isLoading}
            css={override}
            size={12}
          />
          <p style={{ marginTop: "20px" }}>Carregando dados do usuário...</p>
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container className="main-container">
      <ContentContainer>
        <ProfileSection>
          <ImageContainer>
            {getUserImage() ? (
              <UserImage src={getUserImage()} alt="Foto do usuário" />
            ) : (
              <ImagePlaceholder>
                <IconUser size={40} />
              </ImagePlaceholder>
            )}

            <CameraButton>
              <IconCamera size={16} />
              <input
                accept="image/*"
                type="file"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    if (file.size > 4000000) {
                      setNotify({
                        title: "Oops!",
                        message: "Por favor, insira imagens até 4MB!",
                        type: "warning",
                        visible: true,
                      });
                    } else {
                      handleImagePreview(file);
                    }
                  }
                }}
              />
            </CameraButton>
          </ImageContainer>

          <h3 style={{ margin: "0 0 8px 0", color: "#212529" }}>
            {getValues("first_name")
              ? `${getValues("first_name")} ${
                  getValues("last_name") || ""
                }`.trim()
              : "Novo Usuário"}
          </h3>

          {watchedLevel && (
            <LevelBadge level={watchedLevel}>
              {getLevelLabel(watchedLevel)}
            </LevelBadge>
          )}
        </ProfileSection>

        <FormContainer>
          <FormTitle>
            <IconUser size={20} />
            Informações Pessoais
          </FormTitle>

          <form onSubmit={handleSubmit(onSubmit)}>
            <FormGrid>
              <FormGroup>
                <Label>
                  <IconUser size={16} />
                  Primeiro nome
                </Label>
                <Input
                  {...register("first_name", {
                    required: "Por favor, preencha este campo!",
                  })}
                  className={errors.first_name ? "is-invalid" : ""}
                  placeholder="Digite o primeiro nome"
                />
                {errors.first_name && (
                  <ErrorMessage>{errors.first_name.message}</ErrorMessage>
                )}
              </FormGroup>

              <FormGroup>
                <Label>
                  <IconUser size={16} />
                  Sobrenome
                </Label>
                <Input
                  {...register("last_name", {
                    required: "Por favor, preencha este campo!",
                  })}
                  className={errors.last_name ? "is-invalid" : ""}
                  placeholder="Digite o sobrenome"
                />
                {errors.last_name && (
                  <ErrorMessage>{errors.last_name.message}</ErrorMessage>
                )}
              </FormGroup>
            </FormGrid>

            <FormGroup style={{ marginTop: "20px" }}>
              <Label>
                <IconMail size={16} />
                E-mail
              </Label>
              <Input
                {...register("email", {
                  required: "Por favor, preencha este campo!",
                  pattern: {
                    value: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9]+\.[a-zA-Z]{2,}$/,
                    message: "Por favor, insira um e-mail válido!",
                  },
                })}
                className={errors.email ? "is-invalid" : ""}
                placeholder="Digite o e-mail"
                disabled={id ? true : false}
              />
              {errors.email && (
                <ErrorMessage>{errors.email.message}</ErrorMessage>
              )}
            </FormGroup>

            <FormGrid style={{ marginTop: "20px" }}>
              <FormGroup>
                <Label>
                  <IconCalendar size={16} />
                  Aniversário
                </Label>
                <Controller
                  control={control}
                  name="birthday"
                  render={({ field }) => (
                    <NumberFormatInput
                      format="##/##/####"
                      placeholder="DD/MM/AAAA"
                      className={errors.birthday ? "is-invalid" : ""}
                      {...field}
                    />
                  )}
                />
                {errors.birthday && (
                  <ErrorMessage>{errors.birthday.message}</ErrorMessage>
                )}
              </FormGroup>

              <FormGroup>
                <Label>
                  <IconId size={16} />
                  CPF
                </Label>
                <Controller
                  control={control}
                  rules={{
                    required: !id ? "Por favor, preencha este campo!" : false,
                  }}
                  name="cpf"
                  render={({ field }) => (
                    <NumberFormatInput
                      readOnly={id ? true : false}
                      format="###.###.###-##"
                      className={errors.cpf ? "is-invalid" : ""}
                      placeholder="000.000.000-00"
                      {...field}
                    />
                  )}
                />
                {errors.cpf && (
                  <ErrorMessage>{errors.cpf.message}</ErrorMessage>
                )}
              </FormGroup>
            </FormGrid>

            <FormGrid style={{ marginTop: "20px" }}>
              <FormGroup>
                <Label>
                  <IconPhone size={16} />
                  Telefone
                </Label>
                <Controller
                  control={control}
                  rules={{
                    required: "Por favor, preencha este campo!",
                  }}
                  name="phone"
                  render={({ field }) => (
                    <NumberFormatInput
                      format="(##) #####-####"
                      className={errors.phone ? "is-invalid" : ""}
                      placeholder="(00) 00000-0000"
                      {...field}
                    />
                  )}
                />
                {errors.phone && (
                  <ErrorMessage>{errors.phone.message}</ErrorMessage>
                )}
              </FormGroup>

              <FormGroup>
                <Label>
                  <IconShield size={16} />
                  Nível de Acesso
                </Label>
                <Select
                  className={errors.superuser_level ? "is-invalid" : ""}
                  {...register("superuser_level", {
                    required: "Por favor, selecione um nível!",
                  })}
                >
                  <option value="" disabled>
                    Selecione um nível
                  </option>
                  {user.superuser_level === 1 && (
                    <option value="1">Administrador</option>
                  )}
                  <option value="2">Gerente</option>
                  <option value="3">Supervisor</option>
                  <option value="4">Atendente</option>
                </Select>
                {errors.superuser_level && (
                  <ErrorMessage>{errors.superuser_level.message}</ErrorMessage>
                )}
              </FormGroup>
            </FormGrid>

            <StatusToggleContainer>
              <StatusToggle
                active={watchedIsActive === "1"}
                onClick={() =>
                  setValue("is_active", watchedIsActive === "1" ? "0" : "1")
                }
              />
              <StatusLabel>
                Status: {watchedIsActive === "1" ? "Ativo" : "Inativo"}
              </StatusLabel>
            </StatusToggleContainer>

            <SaveButton type="submit" disabled={progress}>
              {progress ? (
                <>
                  <BeatLoader size={8} color="white" />
                  Salvando...
                </>
              ) : (
                <>
                  <IconCheck size={18} />
                  Salvar Usuário
                </>
              )}
            </SaveButton>
          </form>
        </FormContainer>

        {/* Componentes de progresso e notificação */}
        {progress && <ProgressBarComponent />}
        {notify.visible && (
          <AlertResponses
            title={notify.title}
            message={notify.message}
            type={notify.type}
            visible={notify.visible}
            setNotify={setNotify}
          />
        )}
      </ContentContainer>
    </Container>
  );
}
